import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { BRANDING, ONECARE_LOGO } from '../../config/branding';
import { COMPANY_BRANDING } from '../../config/companyBranding';

interface LandingHeaderProps {
  transparent?: boolean;
  fixed?: boolean;
  showCTA?: boolean;
  isCompanyPage?: boolean; // If true, shows company branding; if false/undefined, shows product branding
}

export const LandingHeader: React.FC<LandingHeaderProps> = ({
  transparent = false,
  fixed = true,
  showCTA = true,
  isCompanyPage = false
}) => {
  const location = useLocation();
  // Auto-detect if on company page based on pathname
  const isOnCompanyPage = isCompanyPage || location.pathname === '/';
  // Island navbar (floating rounded bar) on OneCare landing only
  const isOnOneCarePage = location.pathname === '/onecare' || location.pathname === '/onecare/';
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Determine branding based on page context
  const brandName = isOnCompanyPage ? COMPANY_BRANDING.company.name : BRANDING.product.fullName;
  const brandDisplayName = isOnCompanyPage ? 'Aimepic.' : BRANDING.product.name;
  const homeLink = isOnCompanyPage ? '/' : '/onecare/';
  const logoPath = isOnCompanyPage ? '/aimepic-logo.png' : ONECARE_LOGO;
  const navLinks = [
    { id: 'solutions', label: 'Solutions', href: '#solutions' },
    // The company page has no #modules section; send visitors to the OneCare intro instead
    { id: 'modules', label: 'Product Suite', href: isOnCompanyPage ? '#onecare-intro' : '#modules' },
    { id: 'customers', label: 'Customers', href: '#customers' },
    { id: 'updates', label: 'What’s New', href: '#updates' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      setIsScrolled(scrollTop > 20);
    };

    if (fixed) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [fixed]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, callback: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  const useIslandNavbar = isOnOneCarePage || isOnCompanyPage;
  const headerClasses = [
    'landing-header',
    fixed ? 'fixed top-0 left-0 right-0' : 'relative',
    isScrolled || !transparent ? 'scrolled' : '',
    useIslandNavbar ? 'landing-header-island' : '',
    'w-full z-50 transition-all duration-300'
  ].filter(Boolean).join(' ');

  const headerRowContent = (
    <div className="flex justify-between items-center h-[75px] py-2.5 sm:py-3">
      {/* Logo and Brand - compact like main landing */}
      <div className="flex items-center">
        <Link
          to={homeLink}
          className="flex items-center group"
          aria-label={`${brandName} - Go to homepage`}
        >
          <div className="relative flex-shrink-0">
            <img
              src={logoPath}
              alt={`${isOnCompanyPage ? COMPANY_BRANDING.company.name : BRANDING.product.name} logo`}
              className={`relative object-contain drop-shadow-sm ${
                isOnCompanyPage
                  ? 'h-10 w-10 sm:h-11 sm:w-11 company-brand-logo'
                  : 'h-12 w-12 sm:h-14 sm:w-14'
              }`}
              onError={(e) => {
                // Falls straight through to the placeholder below. It used to
                // retry with /logo192.png, but that is now the teal app-icon
                // tile, which must not appear on the site.
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            <div className="logo hidden w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary-500 to-indigo-500 rounded-lg items-center justify-center">
              <SparklesIcon className="h-7 w-7 sm:h-8 sm:w-8 text-white" aria-hidden="true" />
            </div>
          </div>
          <div className={`ml-2.5 sm:ml-3 ${isOnCompanyPage ? 'company-brand-text' : ''}`}>
            <h1
              className={`font-brand font-bold text-gray-900 transition-all duration-300 tracking-tight leading-tight ${
                isOnCompanyPage
                  ? 'company-brand-title'
                  : 'text-base sm:text-lg lg:text-xl xl:text-2xl'
              }`}
            >
              {isOnCompanyPage ? (
                <>Aim<span style={{ color: '#1F749C' }}>epic.</span></>
              ) : (
                <>One<span style={{ color: '#3394D4' }}>Care</span></>
              )}
            </h1>
            <p
              className={`text-xs hidden sm:block font-medium transition-colors duration-300 ${
                isOnCompanyPage ? 'company-brand-tagline text-gray-600' : 'text-gray-600'
              }`}
            >
              {isOnCompanyPage ? COMPANY_BRANDING.company.tagline : BRANDING.product.tagline}
            </p>
          </div>
        </Link>
      </div>
      <nav
        role="navigation"
        aria-label="Main navigation"
        className="hidden md:flex items-center space-x-1 lg:space-x-2"
      >
        {navLinks.map((item) => (
          <a
            key={item.id}
            href={item.href}
            className={`nav-link relative px-2.5 py-1.5 text-[11.2px] lg:text-[12.8px] font-medium text-gray-700 hover:text-gray-900 transition-all duration-200 rounded-lg group ${
              isOnCompanyPage ? 'company-nav-link' : ''
            }`}
            onClick={(e) => {
              if (item.href.startsWith('#')) {
                e.preventDefault();
                handleNavClick(item.href);
              }
            }}
            onKeyDown={(e) => handleKeyDown(e, () => handleNavClick(item.href))}
          >
            {item.label}
            <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-blue-600 group-hover:w-3/4 transition-all duration-300"></span>
          </a>
        ))}
      </nav>
      {showCTA && (
        <div className="hidden md:flex items-center space-x-3">
          <Link
            to="/onecare/login"
            className={`px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors ${
              isOnCompanyPage ? 'company-nav-login' : ''
            }`}
            aria-label={`Go to ${isOnCompanyPage ? 'OneCare' : BRANDING.product.name} login`}
          >
            {isOnCompanyPage ? 'OneCare Login' : 'Login'}
          </Link>
          <Link
            to="/contact"
            className={`inline-flex items-center px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-300 transition-all duration-200 ${
              isOnCompanyPage ? 'company-nav-demo' : ''
            }`}
          >
            Request Demo
          </Link>
        </div>
      )}
      <button
        type="button"
        className="md:hidden p-2.5 rounded-lg text-gray-700 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 transition-all duration-200 hover:bg-gray-100"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        onKeyDown={(e) => handleKeyDown(e, () => setMobileMenuOpen(!mobileMenuOpen))}
        aria-expanded={mobileMenuOpen}
        aria-label="Toggle navigation menu"
        aria-controls="mobile-menu"
      >
        <span className="sr-only">
          {mobileMenuOpen ? 'Close main menu' : 'Open main menu'}
        </span>
        {mobileMenuOpen ? (
          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
        ) : (
          <Bars3Icon className="h-6 w-6" aria-hidden="true" />
        )}
      </button>
    </div>
  );

  return (
    <>
      {/* Skip Link for Accessibility */}
      <a 
        href="#main-content" 
        className="skip-link focus:not-sr-only"
        tabIndex={1}
      >
        Skip to main content
      </a>

      <header role="banner" className={headerClasses}>
        {useIslandNavbar ? (
          <div className="landing-header-island-inner">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {headerRowContent}
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {headerRowContent}
          </div>
        )}
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-menu-title"
      >
        <div 
          className="mobile-menu-content"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile Menu Header */}
          <div className="px-6 py-5 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <h2 id="mobile-menu-title" className="font-brand text-xl font-semibold text-gray-900">
                Menu
              </h2>
              <button
                type="button"
                className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-all duration-200"
                onClick={() => setMobileMenuOpen(false)}
                onKeyDown={(e) => handleKeyDown(e, () => setMobileMenuOpen(false))}
                aria-label="Close navigation menu"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Mobile Navigation Links */}
          <nav role="navigation" aria-label="Mobile navigation" className="px-4 py-6">
            <div className="space-y-1">
              <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Navigate
              </p>
              {navLinks.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  className="group flex items-center px-3 py-3 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"
                  onClick={(e) => {
                    if (item.href.startsWith('#')) {
                      e.preventDefault();
                      handleNavClick(item.href);
                    }
                  }}
                  onKeyDown={(e) => handleKeyDown(e, () => handleNavClick(item.href))}
                >
                  <svg className="w-5 h-5 mr-3 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 12h16M4 16h16" />
                  </svg>
                  {item.label}
                </a>
              ))}

              <div className="my-6 border-t border-gray-200"></div>

              {/* Actions */}
              <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Take Action
              </p>
              <Link
                to="/onecare/login"
                className="group flex items-center px-3 py-3 text-base font-medium text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-200"
                onClick={() => setMobileMenuOpen(false)}
              >
                <ArrowTopRightOnSquareIcon className="w-5 h-5 mr-3 text-blue-600 group-hover:text-blue-700 transition-colors" aria-hidden="true" />
                {isOnCompanyPage ? 'OneCare Login' : 'Platform Login'}
              </Link>
              <Link
                to="/contact"
                className="group flex items-center px-3 py-3 text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-all duration-200"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SparklesIcon className="w-5 h-5 mr-3 text-gray-600" aria-hidden="true" />
                Request a Demo
              </Link>
            </div>
          </nav>
        </div>
        
        {/* Backdrop */}
        <div 
          className="mobile-menu-backdrop absolute inset-0"
          onClick={() => setMobileMenuOpen(false)}
          onKeyDown={(e) => handleKeyDown(e, () => setMobileMenuOpen(false))}
          tabIndex={0}
          role="button"
          aria-label="Close navigation menu"
        />
      </div>
    </>
  );
};
